/**
 * ChoosePath — post-signup landing page where the user picks one of:
 *
 *   1. Free Trial    → POST /api/v1/onboarding/start-trial
 *      Server seeds quotas + dates from app_settings trial defaults
 *      and flips Organization.has_chosen_subscription_path to true.
 *      Then we navigate to / (dashboard).
 *
 *   2. Purchase Plan → navigate to /shop
 *      (Shop's own purchase-success handler flips the flag later.)
 *
 * Router guard (in App.js) ensures users land here only when
 * has_chosen_subscription_path === false. Refreshing this page after
 * the flag flips bounces the user away via the guard.
 *
 * Brand alignment: NoticeAI navy gradient background, two large
 * elevated cards side-by-side on desktop, stacked on mobile. Loading
 * + error states use AntD Skeleton + Alert. Disabled state on the
 * Start Free Trial button when the trial-preview hasn't loaded.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Skeleton,
  Tag,
  Typography,
  message as antMessage,
} from "antd";
import {
  GiftOutlined,
  ShoppingCartOutlined,
  CheckCircleTwoTone,
  ClockCircleOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { fetchData, postData } from "../../api/apiService";
import { ONBOARDING, USERS } from "../../api/endpointMap";
import { useAuth } from "../../context/AuthContext";
import {
  useOnboardingStatus,
  resetOnboardingStatusCache,
} from "../../hooks/useOnboardingStatus";
import { resetSubscriptionStatusCache } from "../../hooks/useSubscriptionStatus";
import { setUserVisiblePortals } from "../../utils/portalAccess";

const { Title, Paragraph, Text } = Typography;

export default function ChoosePath() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isTenantAdmin =
    user?.role === "tenant_admin" || user?.role === "super_admin";

  // Reuse the same hook OnboardingGuard reads from, so a successful
  // start-trial that invalidates the cache reflects in BOTH this page
  // and the guard in one go. Previously we did a local fetchData here
  // — that branched away from the guard's cache and produced an
  // infinite bounce after trial activation (page → / → guard saw
  // stale cache → bounce back here → repeat).
  const { status, isLoading, error: loadError } = useOnboardingStatus();
  const [startingTrial, setStartingTrial] = useState(false);

  // Race-condition guard: if the user already chose a path in another
  // tab while sitting here, redirect away once the hook resolves.
  useEffect(() => {
    if (status?.has_chosen_subscription_path) {
      navigate("/", { replace: true });
    }
  }, [status, navigate]);

  const handleStartTrial = async () => {
    if (!isTenantAdmin) {
      antMessage.warning(
        "Only the organisation admin can start the free trial. Ask your admin to sign in.",
      );
      return;
    }
    setStartingTrial(true);
    try {
      await postData(ONBOARDING.START_TRIAL, {});
      // Critical: invalidate the module-level cache so the guard on
      // the next mount picks up has_chosen=true. Without this the
      // navigate below bounces straight back to /onboarding/choose-path
      // because the guard still sees the stale false flag.
      resetOnboardingStatusCache();
      // Also drop the cached /subscriptions/status so the navbar
      // countdown pill and the /usage page reflect the just-started
      // trial without a refresh.
      resetSubscriptionStatusCache();
      // Rehydrate the user-level visible_portals localStorage cache
      // (originally written by /auth/login, before the trial was
      // started, so it was an empty array). Without this the Dashboard
      // card filter hides every portal card on first paint after the
      // trial activates. Best-effort — failure here doesn't block the
      // toast / navigate; Dashboard's getUserData() also rehydrates.
      try {
        const me = await fetchData(USERS.USER_INFO);
        if (Array.isArray(me?.visible_portals)) {
          setUserVisiblePortals(me.visible_portals);
        }
      } catch (_e) {
        // ignore — Dashboard will rehydrate on mount
      }
      antMessage.success("Free trial activated! Welcome to NoticeAI.");
      navigate("/", { replace: true });
    } catch (e) {
      const detail = e?.detail || e?.message;
      antMessage.error(
        typeof detail === "string"
          ? detail
          : "Failed to start the free trial. Please try again.",
      );
    } finally {
      setStartingTrial(false);
    }
  };

  const handleGoToShop = () => {
    // Shop's purchase-success flow flips has_chosen_subscription_path
    // after Razorpay confirms payment. Until then, the guard still
    // sees has_chosen=false and would bounce the user back here.
    // Shop page itself is gated as accessible during onboarding (see
    // App.js router exemptions).
    navigate("/shop");
  };

  const trial = status?.trial_preview || null;

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #0b1d40 0%, #1e3a8a 50%, #2746b2 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div style={{ width: "100%", maxWidth: 1080 }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <Title level={2} style={{ color: "#fff", marginBottom: 8 }}>
            Welcome to NoticeAI
          </Title>
          <Paragraph
            style={{ color: "rgba(255,255,255,0.8)", marginBottom: 0, fontSize: 16 }}
          >
            Choose how you'd like to get started. You can upgrade your plan
            any time later.
          </Paragraph>
        </div>

        {loadError && (
          <Alert
            type="error"
            showIcon
            message="Could not load onboarding details"
            description={loadError}
            className="mb-4"
          />
        )}

        <div
          className="grid grid-cols-1 md:grid-cols-2 gap-6"
          style={{ alignItems: "stretch" }}
        >
          {/* Free Trial card */}
          <Card
            hoverable
            style={{
              borderRadius: 16,
              boxShadow: "0 12px 32px rgba(0,0,0,0.25)",
              minHeight: 420,
              display: "flex",
              flexDirection: "column",
            }}
            styles={{ body: { flex: 1, display: "flex", flexDirection: "column" } }}
          >
            <div className="flex items-center gap-3 mb-3">
              <GiftOutlined style={{ fontSize: 32, color: "#16a34a" }} />
              <div>
                <Title level={3} style={{ marginBottom: 0 }}>
                  Free Trial
                </Title>
                <Text type="secondary">No credit card required</Text>
              </div>
            </div>

            {isLoading ? (
              <Skeleton active paragraph={{ rows: 4 }} />
            ) : (
              <>
                <div className="mb-3">
                  <Tag
                    color="green"
                    icon={<ClockCircleOutlined />}
                    style={{ fontSize: 14, padding: "4px 10px" }}
                  >
                    {trial?.days ?? "-"} days free
                  </Tag>
                </div>
                <Paragraph style={{ marginBottom: 12 }}>
                  Try NoticeAI for {trial?.days ?? "-"} days with these allowances:
                </Paragraph>
                <ul style={{ paddingLeft: 18, marginBottom: 16, color: "#475569" }}>
                  <li>
                    <strong>Clients:</strong> {trial?.total_clients ?? 0} overall
                    (shared across all portals)
                  </li>
                  <li>
                    <strong>Response generations:</strong>{" "}
                    {trial?.total_responses ?? 0} overall (shared across all
                    portals)
                  </li>
                </ul>
                <div className="mb-3 flex items-center gap-2 text-slate-600">
                  <CheckCircleTwoTone twoToneColor="#16a34a" /> All three portals enabled
                </div>
                <div className="mb-3 flex items-center gap-2 text-slate-600">
                  <CheckCircleTwoTone twoToneColor="#16a34a" /> Full AI features included
                </div>
              </>
            )}

            <div style={{ flex: 1 }} />

            <Button
              type="primary"
              size="large"
              icon={<ArrowRightOutlined />}
              onClick={handleStartTrial}
              loading={startingTrial}
              disabled={isLoading || !!loadError || !isTenantAdmin}
              block
              style={{ height: 48, fontSize: 16 }}
            >
              Start Free Trial
            </Button>
            {!isTenantAdmin && !isLoading && (
              <Text
                type="secondary"
                style={{ marginTop: 8, fontSize: 12, textAlign: "center" }}
              >
                Only the organisation admin can start the trial.
              </Text>
            )}
          </Card>

          {/* Purchase Plan card */}
          <Card
            hoverable
            style={{
              borderRadius: 16,
              boxShadow: "0 12px 32px rgba(0,0,0,0.25)",
              minHeight: 420,
              display: "flex",
              flexDirection: "column",
              borderTop: "4px solid #1e3a8a",
            }}
            styles={{ body: { flex: 1, display: "flex", flexDirection: "column" } }}
          >
            <div className="flex items-center gap-3 mb-3">
              <ShoppingCartOutlined style={{ fontSize: 32, color: "#1e3a8a" }} />
              <div>
                <Title level={3} style={{ marginBottom: 0 }}>
                  Purchase a Plan
                </Title>
                <Text type="secondary">Activate instantly after payment</Text>
              </div>
            </div>

            <Paragraph
              style={{
                marginBottom: 16,
                fontSize: 18,
                lineHeight: 1.7,
                letterSpacing: "0.01em",
                color: "#334155",
              }}
            >
              Pick a plan that fits your firm. Choose the portals and tier
              you need.
            </Paragraph>

            <div style={{ flex: 1 }} />

            <Button
              type="primary"
              size="large"
              icon={<ArrowRightOutlined />}
              onClick={handleGoToShop}
              disabled={isLoading || !!loadError}
              block
              style={{
                height: 48,
                fontSize: 16,
                background: "#1e3a8a",
                borderColor: "#1e3a8a",
              }}
            >
              Go to Shop
            </Button>
          </Card>
        </div>

        <div style={{ textAlign: "center", marginTop: 24 }}>
          <Text style={{ color: "rgba(255,255,255,0.7)" }}>
            Need help deciding? You can switch from trial to a paid plan at any time.
          </Text>
        </div>
      </div>
    </div>
  );
}
